import React from 'react';
import { useDropdownContext } from './Dropdown';

// Helper type to extract props from a component (including ForwardRef components)
type PropsOf<T extends React.ElementType> =
  T extends React.ComponentType<infer P>
    ? P
    : T extends keyof JSX.IntrinsicElements
      ? JSX.IntrinsicElements[T]
      : never;

// DropdownButton doesn't have any props on its own other than the `as` which is defined by the polymorphic types. If
// we ever want to add props to the `DropdownButton` this is where they would be defined
type DropdownButtonOwnProps = object;

// Publicly exposed props with proper polymorphic typing
export type DropdownButtonProps<T extends React.ElementType = 'button'> = DropdownButtonOwnProps & {
  as?: T;
} & Omit<PropsOf<T>, keyof DropdownButtonOwnProps | 'as'>;

/**
 * DropdownButton triggers the dropdown menu to open.
 *
 * This component maintains API compatibility with the original pounce DropdownButton.
 * It supports polymorphic rendering through the `as` prop.
 *
 * @example
 * <DropdownButton as={Button} variant="primary">Actions</DropdownButton>
 */
const DropdownButtonComponent = <T extends React.ElementType = 'button'>(
  { as, children, onClick, ...rest }: DropdownButtonProps<T>,
  ref: React.ForwardedRef<any>,
) => {
  const Component = as || 'button';
  const { handleOpen, isOpen } = useDropdownContext();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    handleOpen(event);
    onClick?.(event as any);
  };

  return (
    <Component
      ref={ref}
      onClick={handleClick}
      aria-expanded={isOpen}
      aria-haspopup="true"
      {...rest}
    >
      {children}
    </Component>
  );
};

export const DropdownButton = React.forwardRef(DropdownButtonComponent) as <
  T extends React.ElementType = 'button',
>(
  props: DropdownButtonProps<T> & { ref?: React.Ref<any> },
) => React.ReactElement;
